import { useCallback, useLayoutEffect, useMemo, useRef } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { useListenerMessage } from '../../../../../hooks/subscription/use-listener-message'
import { useGetOtherProfile } from '../../../../../hooks/queries/profile/use-get-other-profile'
import { useGetMyProfileMini } from '../../../../../hooks/queries/profile/use-get-my-profile-mini'
import { useGlobalStore } from '../../../../../store/global-store'
import type { LiveGuest, User } from '../../../../../types/lives/live'
import { useGetDataSeats } from './use-get-data-seats'

export interface HostUserToGift {
  user: User
  userId: number
  position: number
}

export const useGetUsersToGift = () => {
  const { liveId, liveUserId, typeLive, userIdToSendGift } = useGlobalStore(
    useShallow((state) => ({
      // live: state.live,
      liveId: state.liveId,
      liveUserId: state.liveUserId,
      typeLive: state.typeLive,
      usersToGift: state.usersToGift,
      userIdToSendGift: state.userIdToSendGift,
      // modalGiftOpen: state.modalGiftOpen.visible,
    })),
  )

  const { user: userToSendGift } = useGetOtherProfile(
    userIdToSendGift as number,
  )

  const { user: liveUser } = useGetOtherProfile(liveUserId as number)
  const { formatedDataSeats } = useGetDataSeats()
  const { user: me } = useGetMyProfileMini()
  const myId = me?.id

  // formatedDataSeats é um array NOVO a cada render (filter/sort inline). NÃO pode
  // entrar nas deps de useCallback/useEffect senão vira loop infinito de render (o
  // modal não abria). Guardo num ref sempre fresco e leio no call-time.
  const seatsRef = useRef(formatedDataSeats)
  seatsRef.current = formatedDataSeats

  const keyExtractor = useCallback((item: LiveGuest) => `${item.position}`, [])

  const handleSelectUserToGift = useCallback((id: number) => {
    if (typeof id !== 'number') {
      return console.error(id, 'is not a number')
    }

    const usersToGift = useGlobalStore.getState().usersToGift

    if (usersToGift?.has(id)) {
      return useGlobalStore.setState((state) => {
        const newSet = new Set(state?.usersToGift)
        newSet.delete(id)

        return { usersToGift: newSet }
      })
    }

    return useGlobalStore.setState((state) => {
      const newSet = new Set(state.usersToGift)
      newSet.add(id)

      return { usersToGift: newSet }
    })
  }, [])

  // Default ao abrir o modal: seleciona SÓ o host/dono da sala (ou o user
  // específico, se for envio direto). SET determinístico (antes era toggle via
  // handleSelectUserToGift, que no StrictMode ligava/desligava e ficava instável).
  const selectOnlyFirstUser = useCallback(() => {
    // O desvio "se o alvo padrão sou EU, pré-seleciona o 1º seat que não seja eu"
    // saiu: ele só existia porque o backend rejeitava o auto-presente. O host que
    // abre o modal na própria sala agora fica pré-selecionado em si mesmo.
    const target: number | null | undefined =
      userIdToSendGift && typeof userIdToSendGift === 'number'
        ? userIdToSendGift
        : liveUserId
    useGlobalStore.setState({
      usersToGift: target ? new Set([Number(target)]) : new Set(),
      isSelectedAll: false,
    })
  }, [liveUserId, userIdToSendGift])

  useLayoutEffect(() => {
    selectOnlyFirstUser()
  }, [selectOnlyFirstUser])

  // A cada (re)abertura do modal (RN posta 'load-gifts-packages'), volta a marcar
  // SÓ o primeiro user — senão a seleção anterior (ex.: "todos") persiste.
  useListenerMessage((data: string) => {
    if (data === 'load-gifts-packages') selectOnlyFirstUser()
  })

  const checkGifters = useMemo(() => {
    const isAudioModel = typeLive?.includes('multi')

    const dataOnlyStreamer: HostUserToGift = {
      user: liveUser as User,
      userId: liveUserId!,
      position: 0,
    }

    const usersAllowedsToGift = [dataOnlyStreamer]

    if (!isAudioModel && userIdToSendGift) {
      usersAllowedsToGift.push({
        user: userToSendGift as User,
        userId: userIdToSendGift,
        position: 2,
      })
    }

    const list = isAudioModel ? formatedDataSeats : usersAllowedsToGift

    // MINHA foto na fileira de destinatários. Em live de áudio a lista É a das
    // cadeiras, então eu só aparecia quando estava SENTADO; como espectador não
    // existia entrada minha e não dava pra me escolher. Entro no fim, e só se já
    // não estiver lá (sentado, ou host da própria sala) — senão vira foto repetida.
    const withMe = [...(list ?? [])]
    const jaEstou = withMe.some((g) => Number(g?.userId) === Number(myId))

    if (!jaEstou && myId && me) {
      withMe.push({
        user: me as User,
        userId: Number(myId),
        position: 99,
      })
    }

    if (liveId) {
      return withMe
    }

    if (!liveId) {
      return []
    }
  }, [
    me,
    liveUser,
    liveId,
    liveUserId,
    userIdToSendGift,
    userToSendGift,
    formatedDataSeats,
    typeLive,
    myId,
  ])

  return {
    // live,
    typeLive,
    checkGifters,
    handleSelectUserToGift,
    keyExtractor,
  }
}
